import { Dispatch, SetStateAction, useState } from "react";
import { useSearchParams } from "react-router-dom";
import * as Form from "@radix-ui/react-form";

import { login } from "@/api";
import { Button, Input } from "@/components";
import { includesNumber, includesSpecialCharacter } from "@/utils";
import { useUpdatePassword } from "../hooks/useUpdatePassword.tsx";
import { useTranslate, useAuthToken } from "@/providers/context";

interface ResetPasswordFormProps {
  error: string;
  setError: Dispatch<SetStateAction<string>>;
  setSuccess: Dispatch<SetStateAction<boolean>>;
}

interface FormData {
  password: string;
  passwordConfirm: string;
}

const sanitizeMessage = (msg: string) => {
  if (msg.includes("invalid code")) {
    return "This reset password link is expired or invalid";
  }
  return msg;
};

export const ResetPasswordForm = ({
  error,
  setError,
  setSuccess,
}: ResetPasswordFormProps) => {
  const [searchParams] = useSearchParams();
  const code = searchParams.get("code");
  const updatePassword = useUpdatePassword();
  const { setToken } = useAuthToken();
  const { translate } = useTranslate();
  const [loading, setLoading] = useState(false);

  return (
    <div className="mx-auto">
      <h1 className="h1 mb-[12px] capitalize">{translate("Reset password")}</h1>
      <h5 className="p1-regular sm:h5-regular mb-7 sm:mb-8">
        {translate("Minimum 8 characters, 1 number, 1 special character")}
      </h5>
      <Form.Root
        onSubmit={(event) => {
          setLoading(true);
          event.preventDefault();
          const data = Object.fromEntries(new FormData(event.currentTarget));
          const newPassword = data.password.toString();
          updatePassword.mutate(
            {
              code: code ?? "invalid",
              password: newPassword,
            },
            {
              onError: (error) => {
                setError(
                  (error.response?.data?.detail as string) ??
                    error.message ??
                    "Failed to updated password",
                );
                setLoading(false);
              },
              onSuccess: async ({ email }) => {
                try {
                  const token = await login(email, newPassword);
                  if (token) {
                    setToken(token);
                  }
                } catch (e) {
                  console.error(e);
                  setLoading(false);
                  setSuccess(true); // If login fails, still set success as ResetPasswordSuccess screen will prompt user to sign in
                } finally {
                  setLoading(false);
                  setSuccess(true);
                }
              },
            },
          );
        }}
      >
        <Form.Field name="password" className="mb-[44px] w-full">
          <Form.Message match="valueMissing" className="text-error">
            {translate("Please enter a password")}
          </Form.Message>
          <Form.Message
            className="text-error"
            match={(value) => {
              return (
                value.length < 8 ||
                !includesNumber(value) ||
                !includesSpecialCharacter(value)
              );
            }}
          >
            {translate("Minimum 8 characters, 1 number, 1 special character")}
          </Form.Message>
          <Form.Control asChild required>
            <Input
              className="mb-[52px] sm:mb-11"
              type="password"
              placeholder={translate("New Password")}
            />
          </Form.Control>
        </Form.Field>
        <Form.Field name="passwordConfirm" className="mb-[44px] w-full">
          <Form.Message
            className="text-error"
            match={(value, values) => {
              return (
                value !==
                (Object.fromEntries(values) as unknown as FormData).password
              );
            }}
          >
            {translate("Please enter a matching password")}
          </Form.Message>
          <Form.Control asChild required>
            <Input
              type="password"
              placeholder={translate("Confirm New Password")}
            />
          </Form.Control>
          <Form.Message
            match="typeMismatch"
            forceMatch={!!error}
            className="block h-0 pt-[16px] text-error-item"
          >
            {translate(sanitizeMessage(error))}
          </Form.Message>
        </Form.Field>
        <div className="fixed bottom-0 left-0 right-0 flex flex-col items-center border-t border-border bg-surface-primary px-5 py-4 sm:relative sm:border-none sm:p-0">
          <Form.Submit asChild>
            <Button
              size="l"
              className="text-md h-fit w-full max-w-[440px] px-[14px] py-[10px] capitalize sm:mt-[8px] sm:text-sm"
              disabled={loading}
              loading={loading}
            >
              {translate("Reset password")}
            </Button>
          </Form.Submit>
        </div>
      </Form.Root>
    </div>
  );
};
